import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, MailIcon, PhoneIcon, MapPinIcon, CreditCardIcon, FileTextIcon, ActivityIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Customer, PaymentMethod, CustomerTransaction } from '../types/Customer';
export function CustomerDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'invoices' | 'cards' | 'transactions'>('invoices');
  // Mock data - in a real app, this would be fetched from an API
  const customer: Customer = {
    id: id || '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+60 12 345 6789',
    address: '123 Business Street',
    city: 'Colombo',
    country: 'Sri Lanka',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  };
  const paymentMethods: PaymentMethod[] = [{
    id: '1',
    type: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2025,
    cardholderName: 'John Doe',
    isDefault: true,
    addedAt: '2023-06-15'
  }, {
    id: '2',
    type: 'mastercard',
    last4: '5555',
    expiryMonth: 8,
    expiryYear: 2026,
    cardholderName: 'John Doe',
    isDefault: false,
    addedAt: '2023-09-20'
  }];
  const customerInvoices = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-01-01',
    amount: 125000,
    status: 'paid' as const,
    dueDate: '2024-01-15'
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-015',
    date: '2024-01-15',
    amount: 87500,
    status: 'sent' as const,
    dueDate: '2024-02-01'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-089',
    date: '2023-12-01',
    amount: 150000,
    status: 'paid' as const,
    dueDate: '2023-12-15'
  }];
  const transactions: CustomerTransaction[] = [{
    id: '1',
    date: '2024-01-10',
    description: 'Invoice Payment - INV-2024-001',
    amount: 125000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '1'
  }, {
    id: '2',
    date: '2023-12-10',
    description: 'Invoice Payment - INV-2023-089',
    amount: 150000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '3'
  }, {
    id: '3',
    date: '2023-11-15',
    description: 'Invoice Payment - INV-2023-078',
    amount: 95000,
    status: 'completed',
    paymentMethod: 'Mastercard •••• 5555'
  }, {
    id: '4',
    date: '2023-11-01',
    description: 'Refund - INV-2023-065',
    amount: -25000,
    status: 'refunded',
    paymentMethod: 'Visa •••• 4242'
  }];
  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-600',
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`} data-id="element-5551">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const getCardIcon = (type: string) => {
    return <CreditCardIcon size={20} data-id="element-5552" />;
  };
  return <div className="space-y-6" data-id="element-5553">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5554">
        <button onClick={() => navigate('/customers')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-5555">
          <ArrowLeftIcon size={20} data-id="element-5556" />
          Back to Customers
        </button>
        <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-5557">
          <EditIcon size={18} data-id="element-5558" />
          Edit Customer
        </button>
      </div>
      {/* Customer Info Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5559">
        <div className="flex items-start justify-between" data-id="element-5560">
          <div className="flex items-start gap-4" data-id="element-5561">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xl" data-id="element-5562">
              {customer.name.charAt(0)}
            </div>
            <div data-id="element-5563">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-5564">
                {customer.name}
              </h1>
              <div className="mt-2 space-y-1" data-id="element-5565">
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-5566">
                  <MailIcon size={16} data-id="element-5567" />
                  {customer.email}
                </div>
                {customer.phone && <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-5568">
                    <PhoneIcon size={16} data-id="element-5569" />
                    {customer.phone}
                  </div>}
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-5570">
                  <MapPinIcon size={16} data-id="element-5571" />
                  {customer.address}, {customer.city}, {customer.country}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-5572">
            {getStatusBadge(customer.status)}
            <div className="mt-2 text-sm text-gray-500" data-id="element-5573">
              Customer since{' '}
              {new Date(customer.createdAt).toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>
        {/* Stats */}
        <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5574">
          <div data-id="element-5575">
            <div className="text-sm text-gray-500" data-id="element-5576">Total Spent</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5577">
              MYR {customer.totalSpent.toLocaleString('en-MY')}
            </div>
          </div>
          <div data-id="element-5578">
            <div className="text-sm text-gray-500" data-id="element-5579">Total Invoices</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5580">
              {customer.invoiceCount}
            </div>
          </div>
          <div data-id="element-5581">
            <div className="text-sm text-gray-500" data-id="element-5582">Avg. Invoice Value</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5583">
              MYR{' '}
              {Math.round(customer.totalSpent / customer.invoiceCount).toLocaleString('en-MY')}
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-5584">
        <div className="border-b border-gray-200" data-id="element-5585">
          <nav className="flex -mb-px" data-id="element-5586">
            <button onClick={() => setActiveTab('invoices')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'invoices' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5587">
              <div className="flex items-center gap-2" data-id="element-5588">
                <FileTextIcon size={18} data-id="element-5589" />
                Invoices
              </div>
            </button>
            <button onClick={() => setActiveTab('cards')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'cards' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5590">
              <div className="flex items-center gap-2" data-id="element-5591">
                <CreditCardIcon size={18} data-id="element-5592" />
                Payment Methods
              </div>
            </button>
            <button onClick={() => setActiveTab('transactions')} className={`px-6 py-4 text-sm font-medium border-b-2 ${activeTab === 'transactions' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5593">
              <div className="flex items-center gap-2" data-id="element-5594">
                <ActivityIcon size={18} data-id="element-5595" />
                Transactions
              </div>
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-5596">
          {/* Invoices Tab */}
          {activeTab === 'invoices' && <div data-id="element-5597">
              <div className="overflow-x-auto" data-id="element-5598">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-5599">
                  <thead data-id="element-5600">
                    <tr data-id="element-5601">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5602">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5603">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5604">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5605">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5606">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-5607">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-5608">
                    {customerInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-5609">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900" data-id="element-5610">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-5611">
                          {new Date(invoice.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-5612">
                          {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" data-id="element-5613">
                          MYR {invoice.amount.toLocaleString('en-MY')}
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-5614">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-4 py-4 text-sm text-right" data-id="element-5615">
                          <button onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="text-blue-600 hover:text-blue-900" data-id="element-5616">
                            View
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {/* Payment Methods Tab */}
          {activeTab === 'cards' && <div className="space-y-4" data-id="element-5617">
              <div className="flex justify-between items-center mb-4" data-id="element-5618">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5619">
                  Stored Payment Methods
                </h3>
                <button className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-5620">
                  <PlusIcon size={18} data-id="element-5621" />
                  Add Card
                </button>
              </div>
              {paymentMethods.map(method => <div key={method.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-5622">
                  <div className="flex items-center justify-between" data-id="element-5623">
                    <div className="flex items-center gap-4" data-id="element-5624">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-5625">
                        {getCardIcon(method.type)}
                      </div>
                      <div data-id="element-5626">
                        <div className="flex items-center gap-2" data-id="element-5627">
                          <span className="text-sm font-medium text-gray-900" data-id="element-5628">
                            {method.type.charAt(0).toUpperCase() + method.type.slice(1)}{' '}
                            •••• {method.last4}
                          </span>
                          {method.isDefault && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded" data-id="element-5629">
                              Default
                            </span>}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-5630">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-5631">
                          {method.cardholderName}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-5632">
                      {!method.isDefault && <button className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded" data-id="element-5633">
                          Set as Default
                        </button>}
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded" data-id="element-5634">
                        <TrashIcon size={18} data-id="element-5635" />
                      </button>
                    </div>
                  </div>
                </div>)}
              {paymentMethods.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-5636">
                  No payment methods stored
                </div>}
            </div>}
          {/* Transactions Tab */}
          {activeTab === 'transactions' && <div data-id="element-5637">
              <div className="overflow-x-auto" data-id="element-5638">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-5639">
                  <thead data-id="element-5640">
                    <tr data-id="element-5641">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5642">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5643">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5644">
                        Payment Method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5645">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5646">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-5647">
                    {transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50" data-id="element-5648">
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-5649">
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900" data-id="element-5650">
                          {transaction.description}
                          {transaction.invoiceId && <button onClick={() => navigate(`/invoice-detail/${transaction.invoiceId}`)} className="ml-2 text-blue-600 hover:text-blue-900" data-id="element-5651">
                              View Invoice
                            </button>}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500" data-id="element-5652">
                          {transaction.paymentMethod || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-5653">
                          <span className={transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'} data-id="element-5654">
                            {transaction.amount < 0 ? '-' : ''}MYR{' '}
                            {Math.abs(transaction.amount).toLocaleString('en-MY')}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm" data-id="element-5655">
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}